// src/runtime/ContentStrategyRuntime/experienceService.ts
// ============================================
// Daily Experience Service - 每日经验沉淀（经验驱动选题）
//
// 白皮书第十四章核心主张：「内容主要靠经验和分享才有吸引力，
// 每天沉淀一条经验，而不是每天硬找一个选题」。
// 本服务把"XEAOS 这家 AI 公司是怎么被设计/建造出来的"这一真实过程，
// 作为每日内容素材源：每条经验/决策/踩坑都是天然素材。
//
// 数据来源（全部真实，无 mock）：
//   - 10_Operations（运营层）/Decision/Active_Decisions.md
//   - 10_Operations（运营层）/Daily/*.md
//   - XEAOS/Daily/*.md
//   - XEAOS/Memory/*.md
//   - 30_Inbox/*.md
// ============================================

import * as fs from 'fs';
import * as path from 'path';
import { vaultConfig } from '../shared/vaultConfig';
import { BrowserVaultReader } from '../shared/BrowserVaultReader';

export interface ExperienceItem {
  text: string;
  score: number;
  source: string;
  kind: '经验' | '教训' | '洞察' | '决策' | '方法' | '案例';
}

export interface DailyExperience {
  date: string;
  experiences: ExperienceItem[];
  persistedPath?: string;
}

const SIGNAL_KEYWORDS: Array<[string, number]> = [
  ['经验', 30], ['教训', 30], ['踩坑', 28], ['复盘', 25], ['总结', 22],
  ['发现', 24], ['洞察', 26], ['结论', 24], ['决定', 26], ['决策', 26],
  ['方法', 22], ['方法论', 26], ['流程', 18], ['规则', 20], ['原则', 20],
  ['规律', 24], ['原因', 20], ['效果', 18], ['验证', 20], ['成功', 16],
  ['失败', 22], ['为什么', 18], ['其实', 16], ['关键', 18], ['本质', 24],
  ['而不是', 20], ['不要', 14], ['应该', 14], ['必须', 14], ['最佳实践', 28],
];

const NOISE_KEYWORDS = ['你好', '请问', '好的', '收到', '谢谢', '测试', 'hello', 'hi', '嗯嗯'];

const SOURCE_PATHS = [
  '10_Operations（运营层）/Decision/Active_Decisions.md',
  '10_Operations（运营层）/Daily',
  'XEAOS/Daily',
  'XEAOS/Memory',
  '30_Inbox',
];

export class DailyExperienceService {
  private reader = BrowserVaultReader.getInstance();

  private async readFile(relPath: string): Promise<string | null> {
    try {
      if (vaultConfig.isBrowser) {
        return await this.reader.readFile(relPath);
      }
      const absPath = path.join(vaultConfig.vaultRoot, relPath);
      if (fs.existsSync(absPath)) {
        return fs.readFileSync(absPath, 'utf-8');
      }
      return null;
    } catch {
      return null;
    }
  }

  private async listMd(dirRel: string): Promise<string[]> {
    try {
      if (vaultConfig.isBrowser) {
        const entries = await this.reader.listFiles(dirRel);
        return (entries || [])
          .filter((e: any) => e.path?.endsWith('.md'))
          .map((e: any) => e.path);
      }
      const absDir = path.join(vaultConfig.vaultRoot, dirRel);
      if (!fs.existsSync(absDir)) return [];
      return fs.readdirSync(absDir)
        .filter((f) => f.endsWith('.md'))
        .map((f) => `${dirRel}/${f}`);
    } catch {
      return [];
    }
  }

  private extractSentences(raw: string): string[] {
    return raw
      .split(/\n+|[。！？!?；;]/)
      .map((s) => s.replace(/^[-*>#\s]+/, '').replace(/\s+/g, ' ').trim())
      .filter((s) => s.length >= 10 && s.length <= 120);
  }

  private scoreSentence(s: string): number {
    if (NOISE_KEYWORDS.some((k) => s.includes(k)) && s.length < 30) return 0;
    let score = 10;
    for (const [kw, w] of SIGNAL_KEYWORDS) {
      if (s.includes(kw)) score += w;
    }
    score += Math.min(20, Math.floor(s.length / 10));
    return score;
  }

  private detectKind(s: string): ExperienceItem['kind'] {
    if (/踩坑|教训|失败/.test(s)) return '教训';
    if (/决定|决策|选择/.test(s)) return '决策';
    if (/方法|流程|步骤|SOP/.test(s)) return '方法';
    if (/案例|客户|效果|数据/.test(s)) return '案例';
    if (/经验|规律|本质|洞察|总结|结论/.test(s)) return '洞察';
    return '经验';
  }

  /**
   * 扫描全部经验来源，按评分排序返回 TopN 经验
   */
  async extractToday(limit = 3, sinceDays = 7): Promise<ExperienceItem[]> {
    const all: ExperienceItem[] = [];
    const since = new Date();
    since.setDate(since.getDate() - sinceDays);

    // 1. 单个决策文件
    const decisionRaw = await this.readFile(SOURCE_PATHS[0]);
    if (decisionRaw) {
      for (const s of this.extractSentences(decisionRaw)) {
        const score = this.scoreSentence(s);
        if (score >= 40) {
          all.push({ text: s, score, source: SOURCE_PATHS[0], kind: this.detectKind(s) });
        }
      }
    }

    // 2. 目录扫描（仅取近 sinceDays 天的文件）
    for (const dir of SOURCE_PATHS.slice(1)) {
      const files = await this.listMd(dir);
      for (const f of files) {
        const m = f.match(/(\d{4})-(\d{2})-(\d{2})/);
        if (m) {
          const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
          if (d < since) continue;
        }
        const raw = await this.readFile(f);
        if (!raw) continue;
        for (const s of this.extractSentences(raw)) {
          const score = this.scoreSentence(s);
          if (score >= 40) {
            all.push({ text: s, score, source: f, kind: this.detectKind(s) });
          }
        }
      }
    }

    // 3. 去重 + 按评分排序 + 取 TopN
    const seen = new Set<string>();
    const deduped = all
      .sort((a, b) => b.score - a.score)
      .filter((e) => {
        const key = e.text.slice(0, 25);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    return deduped.slice(0, limit);
  }

  /**
   * 生成并落盘今日经验笔记
   * 路径：10_Operations（运营层）/Daily/经验-{date}.md
   */
  async persistDaily(experiences: ExperienceItem[]): Promise<DailyExperience> {
    const date = new Date().toISOString().slice(0, 10);
    const today = new Date();
    const cnDate = `${today.getMonth() + 1}月${today.getDate()}日`;
    const lines: string[] = [
      `# 💡 每日经验沉淀 · ${cnDate}`,
      '',
      '> 素材来源：XEAOS 这家 AI 公司今天的真实运营过程（决策/日志/复盘/记忆）',
      '> 原则：内容靠经验与分享才有吸引力，每天沉淀一条经验，而不是硬找选题',
      '',
      '## 今日经验',
      '',
    ];
    for (let i = 0; i < experiences.length; i++) {
      const e = experiences[i];
      lines.push(`### ${i + 1}. [${e.kind}] ${e.text}`, '');
      lines.push(`- 评分：${e.score}/100`);
      lines.push(`- 来源：${e.source}`, '');
    }
    lines.push('---', '');
    lines.push('> 由 DailyExperienceService 自动生成 · 经验可直接作为当日内容选题素材');

    const relPath = `10_Operations（运营层）/Daily/经验-${date}.md`;
    try {
      if (vaultConfig.isBrowser) {
        await this.reader.writeFile(relPath, lines.join('\n'));
      } else {
        const absPath = path.join(vaultConfig.vaultRoot, relPath);
        fs.mkdirSync(path.dirname(absPath), { recursive: true });
        fs.writeFileSync(absPath, lines.join('\n'), 'utf-8');
      }
    } catch {
      // 落盘失败不阻断（浏览器降级）
    }

    return { date, experiences, persistedPath: relPath };
  }

  /**
   * 生成可注入 Platform Brief 的经验素材
   */
  async asTopicMaterial(): Promise<{ topic: string; material: string } | null> {
    const experiences = await this.extractToday(1);
    if (experiences.length === 0) return null;
    const top = experiences[0];
    return {
      topic: top.text,
      material: `【今日经验（来自 ${top.source}）】${top.text}\n` +
        `【经验类型】${top.kind}\n` +
        `【素材背景】这是 XEAOS 这家 AI 公司在 ${new Date().toISOString().slice(0, 10)} 的真实运营经验，` +
        `围绕「公开一家 AI 公司是怎么设计出来的」这一主题展开，用真实经历讲清一个道理。`,
    };
  }
}

export const dailyExperienceService = new DailyExperienceService();
export default dailyExperienceService;

